import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import BackButton from './BackButton';
import AccountStatusTab from '../profile/AccountStatusTab';
import { HELP_PATH } from '../../config/moderationLadder';
import NotificationsServiceDB from '../../services/notificationsServiceDB';

// A terminated account may read its notices and nothing else (moderation
// phase 2 arc 3; Kurtis 2026-10-11 Q2): the server refuses every other read
// (accountGate.refuseWrite, TERMINATED_READ_ALLOWLIST). Rather than let every
// page fail one request at a time, the page area says so plainly and offers
// the things still open. They are buttons here because on a phone there
// is no top bar: the bell and sign-out live only on pages this gate replaces
// (self-review 2026-10-11). The notices open through Navigation's
// ?notifications=1 deep link, which works with the top bar hidden.
//
// Arc 4: Account Status is open too — a terminated person must be able to
// appeal and download their data. At /settings?tab=account (where moderation
// notices link) the gate renders that page itself, never SettingsPage, whose
// other tabs read what the server refuses.
const ACCOUNT_STATUS_PATH = '/settings';
const ACCOUNT_STATUS_URL = '/settings?tab=account';
// TAKE IT DOWN reaches anyone (policy §6.8): the server allowlists the
// intake for a terminated account, so the form stays open too (BH-15-02).
const TAKEDOWN_FORM_PATH = '/intimate-image-removal';

function TerminatedAccountGate({ children }) {
  const { currentUser, logout, refreshAccountState } = useAuth();
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const terminated = currentUser?.accountState === 'terminated';

  // An Undo sends the person a notice; nothing they do is refused any more,
  // so nothing else would re-read the account. Listen to the bell (its
  // stream and poll are on the read allowlist) and lift the page with the
  // notice, not when the tab next comes back into view (C143).
  // refreshAccountState drops a call within 5 s of the last one, so each bell
  // update also asks once more after that window: an Undo whose notice lands
  // just after a poll or a mark-read still lifts the page (self-review).
  useEffect(() => {
    if (!terminated || !refreshAccountState) return undefined;
    let again = null;
    const refresh = () => {
      refreshAccountState();
      clearTimeout(again);
      again = setTimeout(refreshAccountState, 5500);
    };
    refresh();
    const unsubscribe = NotificationsServiceDB.subscribe(refresh);
    return () => {
      clearTimeout(again);
      unsubscribe();
    };
  }, [terminated, refreshAccountState]);

  if (!terminated) return children;
  // The published rules a termination notice points to (arc 5): a public
  // page, readable by anyone.
  if (location.pathname === HELP_PATH) return children;
  if (location.pathname === TAKEDOWN_FORM_PATH) return children;

  const openNotices = () => navigate(`${location.pathname}?notifications=1`, { replace: true });
  const signOut = async () => {
    await logout();
    navigate('/');
  };

  const onAccountStatus = location.pathname === ACCOUNT_STATUS_PATH
    && new URLSearchParams(location.search).get('tab') === 'account';
  if (onAccountStatus) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <BackButton
          className="mb-4"
          onClick={() => navigate('/', { replace: true })}
          ariaLabel={t('terminatedPage.backToGate')}
        />
        <AccountStatusTab />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-12 text-center">
      <h1 className="text-xl font-semibold text-content-primary mb-3">
        {t('terminatedPage.title')}
      </h1>
      <p className="text-sm text-content-secondary mb-2">{t('terminatedPage.body')}</p>
      <p className="text-sm text-content-secondary mb-6">{t('terminatedPage.notices')}</p>
      <div className="flex flex-col gap-3">
        <Button variant="primary" onClick={openNotices}>{t('terminatedPage.openNotices')}</Button>
        <Button variant="secondary" onClick={() => navigate(ACCOUNT_STATUS_URL)}>{t('terminatedPage.accountStatus')}</Button>
        <Button variant="secondary" onClick={signOut}>{t('terminatedPage.signOut')}</Button>
      </div>
    </div>
  );
}

export default TerminatedAccountGate;
